import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/contexts/AuthContext';
import { Search, Loader2 } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const FAQs = () => {
  const [faqs, setFaqs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredFaqs, setFilteredFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', label: 'All Questions' },
    { id: 'bills', label: 'Bills' },
    { id: 'committees', label: 'Committees' },
    { id: 'legislators', label: 'Legislators' },
    { id: 'access', label: 'Access' }
  ];

  useEffect(() => {
    fetchFaqs();
  }, []);

  useEffect(() => {
    filterFaqs();
  }, [searchQuery, activeCategory, faqs]);

  const fetchFaqs = async () => {
    try {
      const response = await axios.get(`${API}/faqs`);
      setFaqs(response.data);
      setFilteredFaqs(response.data);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterFaqs = () => {
    let filtered = faqs;

    if (activeCategory !== 'all') {
      filtered = filtered.filter(faq => faq.category === activeCategory);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(faq =>
        faq.question.toLowerCase().includes(query) ||
        faq.answer.toLowerCase().includes(query)
      );
    }

    setFilteredFaqs(filtered);
  };

  const getFaqsByCategory = (category) => {
    return filteredFaqs.filter(faq => faq.category === category);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-700" />
      </div>
    );
  }

  return (
    <div className="py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <h1 className="font-heading text-4xl md:text-5xl font-semibold text-slate-900 mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-slate-600">
            Find answers to common questions about the National Assembly, bills, committees, and access
          </p>
        </div>

        {/* Search */}
        <div className="mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search FAQs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="faq-search-input"
              className="w-full pl-12 pr-4 h-14 rounded-xl border-2 border-slate-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none text-lg"
            />
          </div>
        </div>

        {/* Category Tabs */}
        <Tabs value={activeCategory} onValueChange={setActiveCategory} className="mb-8">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-5 h-auto gap-2 bg-transparent p-0">
            {categories.map(category => {
              const count = category.id === 'all' ? faqs.length : getFaqsByCategory(category.id).length;
              return (
                <TabsTrigger
                  key={category.id}
                  value={category.id}
                  data-testid={`category-tab-${category.id}`}
                  className="rounded-lg border-2 border-slate-200 data-[state=active]:border-primary-700 data-[state=active]:bg-primary-50 data-[state=active]:text-primary-900 py-3"
                >
                  <div className="flex flex-col items-center">
                    <span className="font-medium">{category.label}</span>
                    <span className="text-xs text-slate-500 mt-1">({count})</span>
                  </div>
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>

        {/* FAQs List */}
        {filteredFaqs.length === 0 ? (
          <div className="text-center py-12 bg-surface rounded-xl">
            <p className="text-slate-600 text-lg">No FAQs found matching your criteria.</p>
          </div>
        ) : (
          <Accordion type="single" collapsible className="space-y-4">
            {filteredFaqs.map((faq, index) => (
              <AccordionItem
                key={faq.id}
                value={faq.id}
                className="bg-white border-2 border-slate-200 rounded-xl px-6 shadow-sm hover:shadow-md transition-shadow data-[state=open]:border-primary-500"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left font-semibold text-slate-900 hover:text-primary-700 py-5 hover:no-underline">
                  <span className="pr-4">{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}

        {/* Contact Section */}
        <div className="mt-12 bg-primary-50 rounded-2xl p-8 border border-primary-200">
          <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">
            Can't find what you're looking for?
          </h3>
          <p className="text-slate-700 mb-4">
            If you have additional questions, please contact us:
          </p>
          <ul className="space-y-2 text-slate-700">
            <li className="flex items-center space-x-2">
              <span className="font-semibold">Email:</span>
              <a href="mailto:contact@globalrights.org" className="text-primary-700 hover:text-primary-900">
                contact@globalrights.org
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default FAQs;
